import Button from '@ingka/button';
import InlineMessage from '@ingka/inline-message';
import Loading from '@ingka/loading';
import Text from '@ingka/text';
import { useEffect, useState } from 'react';
import { type AdminUser, listAdminUsers, updateAdminUser } from '@/api/client';
import { useApp } from '@/context/AppContext';
import styles from './AdminUsersPage.module.css';

export default function AdminPage() {
  const { user, catalog } = useApp();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [error, setError] = useState<string>('');

  const load = async () => {
    setLoading(true);
    try {
      const { users } = await listAdminUsers();
      setUsers(users);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: load is unmemoized and intended to run once on mount; adding it would re-fetch on every render.
  useEffect(() => {
    void load();
  }, []);

  const fmtDate = (s: string | null) =>
    s ? new Date(s).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  return (
    <>
      <Text tagName="h1" textStyle="Heading.L" className={styles.pageTitle}>
        User management
      </Text>
      <Text tagName="p" textStyle="Body.L" className={styles.pageSubtitle}>
        Assign product access, promote admins, deactivate users.
      </Text>

      {error && (
        <div className={styles.errorWrap}>
          <InlineMessage variant="negative" title="Error" body={error} />
        </div>
      )}

      {loading ? (
        <div className={styles.loadingWrap}>
          <Loading text="Loading users..." />
        </div>
      ) : (
        <div className={styles.tableCard}>
          <table className={styles.table}>
            <thead className={styles.thead}>
              <tr>
                <th className={styles.th}>Name</th>
                <th className={styles.th}>Email</th>
                <th className={styles.th}>Tenant</th>
                <th className={styles.th}>Role</th>
                <th className={styles.th}>Products</th>
                <th className={styles.th}>Active</th>
                <th className={styles.th}>Last login</th>
                <th className={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u.user_sub === user?.sub;
                return (
                  <tr key={u.id} className={styles.tr}>
                    <td className={styles.td}>
                      {u.name || '—'}
                      {isSelf && <span className={styles.selfTag}>(you)</span>}
                    </td>
                    <td className={styles.td}>{u.email}</td>
                    <td className={styles.td}>
                      {u.tenant === 'inter_ikea' ? 'Inter IKEA' : 'Other'}
                    </td>
                    <td className={styles.td}>
                      <span
                        className={`${styles.roleBadge} ${u.role === 'admin' ? styles.roleBadgeAdmin : ''}`}
                      >
                        {u.role}
                      </span>
                    </td>
                    <td className={styles.td}>
                      {u.product_scope.length === 0 ? (
                        <em className={styles.scopeNone}>none</em>
                      ) : u.product_scope.includes('*') ? (
                        'all'
                      ) : (
                        u.product_scope.join(', ')
                      )}
                    </td>
                    <td className={styles.td}>
                      {u.active ? (
                        <span className={styles.activeOn}>●</span>
                      ) : (
                        <span className={styles.activeOff}>●</span>
                      )}
                    </td>
                    <td className={styles.td}>{fmtDate(u.last_login)}</td>
                    <td className={styles.tdRight}>
                      <Button
                        type="secondary"
                        size="small"
                        text="Edit"
                        onClick={() => setEditing(u)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <EditUserModal
          user={editing}
          isSelf={editing.user_sub === user?.sub}
          products={catalog?.products ?? []}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </>
  );
}

// ─── Modal ───────────────────────────────────────────────────────────────────

function EditUserModal({
  user,
  isSelf,
  products,
  onClose,
  onSaved,
}: {
  user: AdminUser;
  isSelf: boolean;
  products: Array<{ id: string; name: string }>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [role, setRole] = useState(user.role);
  const [scope, setScope] = useState<string[]>(user.product_scope);
  const [active, setActive] = useState(user.active);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggleProduct = (id: string) => {
    setScope((cur) => (cur.includes(id) ? cur.filter((p) => p !== id) : [...cur, id]));
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const updates: { role?: 'uploader' | 'admin'; product_scope?: string[]; active?: boolean } =
        {};
      if (role !== user.role) updates.role = role;
      if (JSON.stringify(scope) !== JSON.stringify(user.product_scope))
        updates.product_scope = scope;
      if (active !== user.active) updates.active = active;

      if (Object.keys(updates).length === 0) {
        onClose();
        return;
      }
      await updateAdminUser(user.id, updates);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
      setSaving(false);
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <Text tagName="h2" textStyle="Heading.L" className={styles.modalTitle}>
          Edit user
        </Text>
        <Text tagName="p" textStyle="Body.M" className={styles.modalSubtitle}>
          {user.email}
        </Text>

        {error && (
          <div className={styles.modalError}>
            <InlineMessage variant="negative" body={error} />
          </div>
        )}

        <div className={styles.field}>
          <p className={styles.fieldLabel}>Role</p>
          <div className={styles.radioGroup}>
            <label>
              <input
                type="radio"
                checked={role === 'uploader'}
                onChange={() => setRole('uploader')}
                disabled={isSelf && user.role === 'admin'}
              />{' '}
              Uploader
            </label>
            <label>
              <input type="radio" checked={role === 'admin'} onChange={() => setRole('admin')} />{' '}
              Admin
            </label>
          </div>
          {isSelf && user.role === 'admin' && (
            <p className={styles.fieldNote}>You cannot demote yourself.</p>
          )}
        </div>

        <div className={styles.field}>
          <p className={styles.fieldLabel}>
            Products
            {role === 'admin' && <span style={{ fontWeight: 400 }}> (admins have all)</span>}
          </p>
          {role === 'admin' ? (
            <p className={styles.adminNote}>All products</p>
          ) : (
            <div className={styles.productGrid}>
              {products.map((p) => (
                <label
                  key={p.id}
                  className={`${styles.productChip} ${scope.includes(p.id) ? styles.productChipSelected : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={scope.includes(p.id)}
                    onChange={() => toggleProduct(p.id)}
                    style={{ marginRight: 6 }}
                  />
                  {p.name}
                </label>
              ))}
            </div>
          )}
        </div>

        <div className={styles.activeField}>
          <label>
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              disabled={isSelf}
              style={{ marginRight: 6 }}
            />
            Active
          </label>
          {isSelf && <p className={styles.fieldNote}>You cannot deactivate yourself.</p>}
        </div>

        <div className={styles.modalActions}>
          <Button type="secondary" text="Cancel" onClick={onClose} disabled={saving} />
          <Button
            type="emphasised"
            text={saving ? 'Saving...' : 'Save'}
            onClick={save}
            disabled={saving}
          />
        </div>
      </div>
    </div>
  );
}
